import classnames from "classnames";
import React from "react";
import { Avatar, CircleExpandButton } from "../../atoms/atoms";

import "./profileExpand.scss";

interface IProps {
  user: IUserPublic;
  className?: string;
  inGrid?: {
    isExpanded: boolean;
    handleExpansion: () => void;
    order: number;
    row: number;
  };
}

const ProfileExpand: React.FC<IProps> = ({ user, className, inGrid }: IProps) => {
  const { id, image, profileHtml, firstName, lastName, title } = user;

  const divRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const isInGrid: boolean = inGrid != null;
  const profileExpanded: boolean = isInGrid ? inGrid.isExpanded : expanded;
  const expandDisabled: boolean = profileHtml == null;
  const textOrder: number = isInGrid && inGrid.isExpanded && inGrid.row + 1;

  const textClass = classnames("profile-expand__text", {
    "profile-expand__text--hidden": !profileExpanded,
    "column is-10 is-offset-1 profile-expand__text--active": profileExpanded,
  });

  const dotClass = classnames("profile-expand__dot", { "profile-expand__dot--active": profileExpanded });
  const avatarClass = classnames("profile-expand__avatar", { "profile-expand__avatar--active": profileExpanded });

  const expandProfile = (e: React.MouseEvent<(HTMLButtonElement | HTMLElement), MouseEvent>) => {
    e.preventDefault();
    if (expandDisabled) {
      return null;
    }
    if (inGrid) {
      inGrid.handleExpansion(); // collapse if already open
    } else {
      setExpanded(!expanded);
    }
    divRef.current.focus();
  };

  return (
    <>
      <section
        className={classnames(`profile-expand column ${className || ""}`, { "profile-expand__disabled": expandDisabled })}
        style={{ order: isInGrid && inGrid.order }}
        onClick={(e) => expandProfile(e)}
        title={`Find out more about ${firstName} ${lastName} - ${title || ""}`}
        data-scroll-id={id}
      >
        <div className="profile-expand__content">
          <Avatar
            className={avatarClass}
            source={image && image.thumbnailUrl}
            rounded={true}
            title={firstName}
            alt={`${firstName} - ${title || ""} at TravelLocal`}
          />

          <div className="profile-expand__info">
            <h4 className="profile-expand__name">{`${firstName} ${lastName}`}</h4>
            {title && <h5 className="profile-expand__title is-heading text--mid text--caps">{title}</h5>}
            {!expandDisabled &&
              <CircleExpandButton label={`Meet ${firstName}`} onClick={(e) => expandProfile(e)} active={profileExpanded} controlsId={id.toString()} />
            }
          </div>
          {!expandDisabled && (
            <>
              <div className="u-flex-break"></div>
              <div className="profile-expand__dots">
                <div className={dotClass}></div>
                <div className={dotClass}></div>
                <div className={dotClass}></div>
                <div className={dotClass}></div>
              </div>
            </>
          )}
        </div>
      </section>
      <section
        ref={divRef}
        className={textClass}
        dangerouslySetInnerHTML={{ __html: `<div class="profile-expand__text-wrap">${profileHtml}</div>`}}
        style={{ order: textOrder }}
        role="region"
        tabIndex={-1}
        aria-labelledby={`btn-${id.toString()}`}
        aria-hidden={!profileExpanded}
        id={id.toString()}
      />
    </>
  );
};

export default ProfileExpand;
